import { useEffect, useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

/**
 * Sitewide custom 404 page, recreating the original restorehair.com 404:
 * centered "dropped the ball" headline, 404 illustration, and a blog search box.
 */
export function NotFoundPage() {
  const router = useRouter();
  const [query, setQuery] = useState("");

  // 404s should never be indexed and need their own title.
  useEffect(() => {
    document.title = "Page Not Found | RESTORE Hair";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim().slice(0, 100);
    if (q) {
      router.navigate({ to: "/hair-loss-blog/", search: { s: q } });
    } else {
      router.navigate({ to: "/hair-loss-blog/" });
    }
  }

  return (
    <>
      <Header />
      <main data-cta-location="not_found" className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Oops! Looks like we dropped the ball!
          </h1>
          <img
            src="/404-football.webp"
            alt="404 — football player standing in the zero"
            width={1200}
            height={515}
            loading="lazy"
            decoding="async"
            className="mx-auto mt-8 w-full max-w-2xl"
          />
          <p className="mt-8 text-lg font-bold text-foreground">
            It seems we can't find what you're looking for.{" "}
            <Link to="/contact-us/" className="underline underline-offset-2 hover:opacity-80">
              Contact us
            </Link>{" "}
            with any questions.
          </p>
          <form
            onSubmit={onSearch}
            role="search"
            className="mx-auto mt-6 flex max-w-md overflow-hidden rounded-md border border-input bg-background"
          >
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Here..."
              aria-label="Search the blog"
              maxLength={100}
              className="h-12 flex-1 bg-background px-4 text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
            <button
              type="submit"
              className="inline-flex h-12 items-center gap-2 bg-primary px-5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              Search the Site
            </button>
          </form>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/hair-loss-blog/" className="text-primary hover:underline">Hair Loss Blog</Link>
            <Link to="/pricing/" className="text-primary hover:underline">Pricing</Link>
            <Link to="/locations/restore-denver/" className="text-primary hover:underline">Locations</Link>
            <Link to="/contact-us/" className="text-primary hover:underline">Contact Us</Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
